import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ReceiptText, RefreshCw, Search, Zap, Loader2, CheckCircle2, AlertTriangle, MinusCircle } from "lucide-react";
import { parasutApi, errMsg } from "../../lib/parasutApi";
import { DOC_LABEL, money, today } from "./invoiceDefaults";
import { StatusBadge } from "./InvoiceHistory";
import { InvoiceDialog } from "./InvoiceDialog";
import { runBulk } from "./bulkInvoice";

const inp = "h-9 px-3 rounded-lg border border-slate-300 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/40";
const ACTIVE = ["creating", "processing", "approved"];

function BulkResult({ r }) {
  if (!r) return null;
  const map = {
    running: [Loader2, "text-indigo-600 animate-spin", "Kesiliyor…"],
    ok: [CheckCircle2, "text-emerald-600", r.reason],
    skip: [MinusCircle, "text-amber-600", r.reason],
    error: [AlertTriangle, "text-red-600", r.reason],
  };
  const [Icon, cls, txt] = map[r.status] || map.error;
  return <span className="inline-flex items-center gap-1 text-[11px] font-medium"><Icon size={13} className={cls} /> <span className={cls.replace(" animate-spin", "")}>{txt}</span></span>;
}

function usePollProcessing(rows, reload) {
  const pending = rows.filter((r) => r.invoice?.status === "processing").map((r) => r.invoice.id);
  const key = pending.join(",");
  const ref = useRef(reload); ref.current = reload;
  useEffect(() => {
    if (!key) return undefined;
    const t = setInterval(async () => {
      await Promise.all(key.split(",").map((id) => parasutApi.refresh(id).catch(() => null)));
      ref.current(true);
    }, 4000);
    return () => clearInterval(t);
  }, [key]);
}

export function BillableList() {
  const [date, setDate] = useState(today());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [onlyOpen, setOnlyOpen] = useState(true);
  const [channel, setChannel] = useState("");
  const [sel, setSel] = useState(new Set());
  const [bulk, setBulk] = useState({});
  const [running, setRunning] = useState(false);
  const [openId, setOpenId] = useState(null);

  const reqRef = useRef(0);
  const load = useCallback(async (silent) => {
    const my = ++reqRef.current; // gec gelen eski tarih cevabi yeni tarihin listesini ezmesin
    if (!silent) setLoading(true);
    try { const data = await parasutApi.billable(date); if (my === reqRef.current) setRows(data); }
    catch (e) { if (my === reqRef.current) toast.error(errMsg(e)); }
    finally { if (my === reqRef.current) setLoading(false); }
  }, [date]);
  useEffect(() => { load(); setSel(new Set()); setBulk({}); }, [load]);
  usePollProcessing(rows, load);

  const channels = useMemo(() => [...new Set(rows.map((r) => r.channel))].sort(), [rows]);
  const shown = useMemo(() => rows.filter((r) => {
    if (onlyOpen && ACTIVE.includes(r.invoice?.status)) return false;
    if (channel && r.channel !== channel) return false;
    const t = q.trim().toLowerCase();
    return !t || [r.customer_name, r.confirmation_code, r.product_name, r.country].some((v) => (v || "").toLowerCase().includes(t));
  }), [rows, onlyOpen, channel, q]);
  const selectable = shown.filter((r) => !ACTIVE.includes(r.invoice?.status));
  const allSel = selectable.length > 0 && selectable.every((r) => sel.has(r.booking_id));
  const toggle = (id) => setSel((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = () => setSel(allSel ? new Set() : new Set(selectable.map((r) => r.booking_id)));

  const startBulk = async () => {
    const ids = [...sel];
    if (!ids.length) return;
    if (!window.confirm(`${ids.length} rezervasyon için RESMİ e-Arşiv / e-Fatura kesilecek.\nÜlkesi belirsiz, tutarı olmayan veya eksi kalan olanlar atlanır.\nOnaylıyor musunuz?`)) return;
    setRunning(true); setBulk({});
    const res = await runBulk(ids, (p) => setBulk((b) => ({ ...b, [p.id]: p })));
    const vals = Object.values(res);
    toast.success(`Toplu kesim bitti · ${vals.filter((v) => v.status === "ok").length} gönderildi · ${vals.filter((v) => v.status === "skip").length} atlandı · ${vals.filter((v) => v.status === "error").length} hata`);
    setRunning(false); setSel(new Set()); load(true);
  };

  return (
    <div data-testid="billable-list" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <input data-testid="bill-date" type="date" className={inp} value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
        <div className="relative"><Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
          <input data-testid="bill-search" className={`${inp} pl-8 w-64`} placeholder="Müşteri, kod, tur, ülke…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <select data-testid="bill-channel" className={inp} value={channel} onChange={(e) => setChannel(e.target.value)}>
          <option value="">Tüm kanallar</option>{channels.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
          <input data-testid="bill-only-open" type="checkbox" checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} className="w-4 h-4 accent-indigo-600" /> Sadece faturasızlar
        </label>
        <button data-testid="bill-reload" onClick={() => load()} className="h-9 px-3 rounded-full border border-slate-300 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-100 inline-flex items-center gap-1.5 transition-colors">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Yenile
        </button>
        <div className="ml-auto flex items-center gap-3">
          <span data-testid="bill-count" className="text-sm text-slate-500">{shown.length} rezervasyon · {sel.size} seçili</span>
          <button data-testid="bill-bulk-btn" disabled={!sel.size || running} onClick={startBulk}
            className="h-9 px-4 rounded-full bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 disabled:opacity-40 inline-flex items-center gap-1.5 transition-colors">
            {running ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />} Seçilenleri Toplu Kes
          </button>
        </div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-auto max-h-[70vh]">
        <table className="w-full text-sm text-slate-700">
          <thead className="sticky top-0 bg-slate-100 text-[11px] uppercase tracking-wider text-slate-500 z-10">
            <tr>
              <th className="px-3 py-2 w-8"><input data-testid="bill-select-all" type="checkbox" checked={allSel} onChange={toggleAll} className="w-4 h-4 accent-indigo-600" /></th>
              {["Saat", "Kod", "Müşteri", "Ülke", "Tur", "Kanal", "Kişi", "Tutar", "Fatura", ""].map((h) => <th key={h} className="px-3 py-2 text-left font-semibold">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={11} className="px-3 py-10 text-center text-slate-400">{loading ? "Yükleniyor…" : "Bu günde faturalanacak rezervasyon yok."}</td></tr>}
            {shown.map((r) => <BillRow key={r.booking_id} r={r} checked={sel.has(r.booking_id)} onToggle={toggle} onOpen={setOpenId} bulk={bulk[r.booking_id]} />)}
          </tbody>
        </table>
      </div>
      {openId && <InvoiceDialog bookingId={openId} open onOpenChange={(o) => { if (!o) { setOpenId(null); load(true); } }} />}
    </div>
  );
}

function BillRow({ r, checked, onToggle, onOpen, bulk }) {
  const inv = r.invoice;
  const done = ACTIVE.includes(inv?.status);
  return (
    <tr data-testid={`bill-row-${r.booking_id}`} className={`border-t border-slate-100 transition-colors ${checked ? "bg-indigo-50/60" : "hover:bg-slate-50"}`}>
      <td className="px-3 py-2"><input data-testid={`bill-check-${r.booking_id}`} type="checkbox" disabled={done} checked={checked} onChange={() => onToggle(r.booking_id)} className="w-4 h-4 accent-indigo-600 disabled:opacity-30" /></td>
      <td className="px-3 py-2 tabular-nums text-xs">{r.booking_time || "—"}</td>
      <td className="px-3 py-2 font-mono text-xs">{r.confirmation_code || "—"}</td>
      <td className="px-3 py-2 font-medium text-slate-800">{r.customer_name || "—"}</td>
      <td className="px-3 py-2 text-xs">
        <span className={r.country_warning ? "text-amber-700" : "text-slate-700"} title={r.country_warning || ""}>
          {r.country || "?"} {r.country_warning && <AlertTriangle size={12} className="inline -mt-0.5" />}
        </span>
        <div className="text-[10px] text-slate-400">{r.is_abroad ? "yabancı" : "yerli"}</div>
      </td>
      <td className="px-3 py-2 text-xs text-slate-600 max-w-[240px] truncate" title={r.option_name || r.product_name}>{r.option_name || r.product_name}</td>
      <td className="px-3 py-2 text-xs">{r.channel} <span className="text-slate-400">({r.channel_prefix})</span></td>
      <td className="px-3 py-2 tabular-nums">{r.total_pax}</td>
      <td data-testid={`bill-amount-${r.booking_id}`} className="px-3 py-2 tabular-nums font-semibold">{r.amount ? money(r.amount, r.currency) : <span className="text-red-500 text-xs">tutar yok</span>}</td>
      <td className="px-3 py-2">
        {inv ? <div className="flex flex-col gap-0.5"><StatusBadge status={inv.status} testId={`bill-status-${r.booking_id}`} />
          {inv.invoice_no && <span className="font-mono text-[10px] text-slate-500">{DOC_LABEL[inv.doc_type]} {inv.invoice_no}</span>}</div>
          : <span className="text-xs text-slate-400">Faturasız</span>}
        <BulkResult r={bulk} />
      </td>
      <td className="px-3 py-2 text-right">
        <button data-testid={`bill-open-${r.booking_id}`} onClick={() => onOpen(r.booking_id)}
          className={`h-8 px-3 rounded-full text-xs font-bold inline-flex items-center gap-1 transition-colors ${done ? "border border-slate-300 text-slate-600 hover:bg-slate-100" : "bg-indigo-600 text-white hover:bg-indigo-700"}`}>
          <ReceiptText size={13} /> {done ? "Aç" : "Fatura Kes"}
        </button>
      </td>
    </tr>
  );
}
